// src/pages/Services.js
import React, { useMemo, useState } from "react";
import "../index.css";
import useSEO from "../seo/useSEO";

/* Accordéon simple */
function Accordion({ title, children }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="accordion">
      <summary onClick={() => setOpen(!open)} aria-expanded={open}>
        {title}
        <span className="acc-caret">{open ? "⌄" : "›"}</span>
      </summary>
      {open && <div className="accordion-content">{children}</div>}
    </div>
  );
}

/* Données des services (version gastronomique) */
const SERVICES = [
  {
    key: "produit",
    title: "Shooting culinaire – Plats & cartes",
    img: "/images/services/service1.jpg",
    blurb:
      "Photographies de vos plats signatures, menus dégustation et cartes saisonnières, avec une lumière maîtrisée et un stylisme sobre pour des visuels prêts à l’emploi sur vos menus, votre site et vos réseaux sociaux.",
    details: [
      "Travail sur la lumière, les textures et les dressages",
      "Séries cohérentes pour cartes, sites et réseaux sociaux",
      "Exports optimisés pour le web et le print",
    ],
    gains: [
      "Valorise votre carte et vos plats signatures",
      "Renforce l’attrait visuel de votre restaurant ou traiteur",
      "Crée une banque d’images cohérente pour votre communication",
    ],
  },
  {
    key: "portrait",
    title: "Portraits de chefs & brigades",
    img: "/images/services/service2.jpg",
    blurb:
      "Portraits posés ou en action pour chefs, brigades et équipes. Idéal pour la presse, les distinctions, les pages “À propos” et les réseaux sociaux.",
    details: [
      "Portraits individuels et de groupe",
      "Prise de vue en cuisine, en salle ou dans un décor choisi",
      "Retouches fines et naturelles",
    ],
    gains: [
      "Incarne votre maison à travers vos visages",
      "Renforce la confiance et la proximité avec vos clients",
      "Images adaptées à la presse, aux guides et à votre site",
    ],
  },
  {
    key: "moment",
    title: "Reportage restaurant & service",
    img: "/images/services/service3.jpg",
    blurb:
      "Reportage vivant au cœur de votre établissement : cuisine, dressages, service, ambiance et détails d’arts de la table. Pour raconter l’expérience que vous proposez à vos clients.",
    details: [
      "Plans larges, détails, gestes et ambiances",
      "Adaptation aux contraintes de service et d’horaires",
      "Livrables rapides et prêts à publier",
    ],
    gains: [
      "Raconte votre histoire au-delà des assiettes",
      "Crée du contenu riche pour site, réseaux et dossiers",
      "Renforce l’authenticité de votre communication",
    ],
  },
  {
    key: "lieu",
    title: "Ambiances & lieux",
    img: "/images/services/service4.jpg",
    blurb:
      "Mise en valeur de vos espaces : salle de restaurant, bar, terrasse, cuisine ouverte, ateliers, salons privés… pour des visuels harmonieux et adaptés au web et au print.",
    details: [
      "Horaires choisis en fonction de la lumière naturelle",
      "Corrections de perspectives et gestion des déformations",
      "Fichiers optimisés pour votre site et vos supports imprimés",
    ],
    gains: [
      "Valorise l’expérience que vit votre client dès son arrivée",
      "Améliore votre image sur votre site et Google Maps",
      "Contribue à une identité visuelle homogène",
    ],
  },
];

const FILTERS = [
  { key: "all", label: "Tout" },
  { key: "produit", label: "Plats & cartes" },
  { key: "portrait", label: "Portraits" },
  { key: "moment", label: "Reportage" },
  { key: "lieu", label: "Lieux & ambiances" },
];

/* Mailto prérempli */
function buildMailto(serviceTitle) {
  const to = "contact@maximeh.fr";
  const subject = `Demande de devis – ${serviceTitle}`;
  const body = [
    "Bonjour Maxime,",
    "",
    `Je souhaite un devis pour le service : ${serviceTitle}.`,
    "",
    "📌 Projet : ",
    "📍 Lieu : ",
    "🗓️ Date(s) souhaitée(s) : ",
    "💶 Budget indicatif : ",
    "🖼️ Utilisation des images (web / réseaux / print) : ",
    "",
    "Merci,",
    "Nom / Société",
    "Téléphone",
    "",
    "--",
    "Maxime H – Photographe Culinaire",
    "💻 www.maximeh.fr",
    "✉️ contact@maximeh.fr",
    "📱 +33663252202",
  ].join("\n");

  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function Services() {
  // ✅ SEO
  useSEO({
    title: "Services — Maxime H, photographe culinaire",
    description:
      "Photographe culinaire : shootings de plats, portraits de chefs, reportages restaurant et mise en valeur de lieux. Images prêtes pour le web, les réseaux sociaux et le print.",
    canonical: `${window.location.origin}/services`,
    image: "/images/og-default.jpg",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Photographie culinaire",
      provider: {
        "@type": "Person",
        name: "Maxime H",
        telephone: "+33663252202",
        url: "https://www.maximeh.fr",
      },
      areaServed: ["FR", "BE", "LU"],
    },
  });

  const [active, setActive] = useState("all");

  const items = useMemo(() => {
    if (active === "all") return SERVICES;
    return SERVICES.filter((s) => s.key === active);
  }, [active]);

  return (
    <div className="services-page">
      {/* Intro */}
      <section className="services-intro">
        <p className="services-subtitle">
          Des images gastronomiques au service de votre carte, de votre lieu et de votre histoire.
        </p>
        <p className="services-lead">
          En tant que photographe culinaire spécialisé, j’accompagne les chefs, restaurants, hôtels,
          traiteurs et artisans dans la création d’images destinées à leur communication : menus,
          sites internet, réseaux sociaux, supports imprimés et presse. Chaque prestation est pensée
          pour s’adapter à votre réalité et à vos objectifs : valoriser une carte, raconter un lieu,
          incarner une équipe ou créer une banque d’images cohérente sur la durée.
        </p>
      </section>

      {/* Filtres */}
      <div className="svc-filter" role="tablist" aria-label="Filtre des services">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={active === f.key}
            aria-controls={`panel-${f.key}`}
            className={`chip ${active === f.key ? "active" : ""}`}
            onClick={() => setActive(f.key)}
          >
            {f.label}
          </button>
        ))}
        <span className="svc-count" aria-live="polite">
          {items.length} {items.length > 1 ? "services" : "service"}
        </span>
      </div>

      {/* Grille (centrée si un seul item) */}
      <section
        className={`services-grid fade-in ${items.length === 1 ? "single" : ""}`}
        id={`panel-${active}`}
        role="tabpanel"
        aria-labelledby={active}
      >
        {items.map((s) => (
          <article className="service-item card-pop" key={s.key}>
            <img
              src={s.img}
              alt={s.title}
              className="svc-thumb"
              loading="lazy"
            />

            {/* Bloc titre + intro avec hauteur fixe pour alignement */}
            <div className="svc-header-fixed">
              <h2 className="svc-title">{s.title}</h2>
              <p className="svc-blurb">{s.blurb}</p>
            </div>

            {/* Accordéons */}
            <div className="service-acc">
              <Accordion title="Description">
                <p>{s.blurb}</p>
              </Accordion>

              <Accordion title="Détail">
                <ul>
                  {s.details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </Accordion>

              <Accordion title="Avantages">
                <ul>
                  {s.gains.map((g, i) => (
                    <li key={i}>{g}</li>
                  ))}
                </ul>
              </Accordion>
            </div>

            {/* Bouton devis */}
            <div className="svc-cta-wrap">
              <a href={buildMailto(s.title)} className="btn-ghost">
                Demander un devis
              </a>
            </div>
          </article>
        ))}
      </section>

      {/* CTA final — texte + bouton dessous */}
      <div className="cta-section">
        <h2>On en parle ?</h2>
        <p>
          Expliquez-moi votre besoin, je vous propose une approche et un chiffrage rapidement.
        </p>
        <a href={buildMailto("Projet")} className="cta-button">
          Contact rapide
        </a>
      </div>
    </div>
  );
}